// B-89 — each office's own email addresses, kept from the Oficinas screen: the addresses an office's
// own closing report goes to ("Enviar correo de oficina", from the panel).
//
// Two ways in, both saved by `saveCierreOfficeContacts` (all or nothing, `cierres.offices.contacts`
// per office):
//  - `EditarCorreosOficina`: one office, its list typed (commas, semicolons, spaces or lines).
//  - `CargarCorreosExcel`: every office at once from an Excel — a column with the office code and
//    one or more with addresses. What was read is shown against the client's offices before
//    anything is saved; "Descargar plantilla" gives the sheet already filled with today's list.
import { useState } from "react";
import * as XLSX from "xlsx";
import { Download, FileUp, Mail } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  cn,
} from "@mantis-sas/ui";

import { saveCierreOfficeContacts } from "../../../ApiCalls/mantisBusiness";
import { correosEscritos } from "../../../utils/cierreConsolidado";
import { oficinasDeExcel, revisarCarga } from "../../../utils/cierreCorreosOficina";
import { correctionError } from "../../../utils/cierreCorrections";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const errorDe = (response) =>
  response?.errorCode || response?.message ? correctionError(response) : "No se pudieron guardar los correos.";

export function EditarCorreosOficina({ oficina, token, mantisBusinessId, clientCode, onClose, onSaved }) {
  const [texto, setTexto] = useState((oficina?.emails || []).join("\n"));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const { correos, invalidos } = correosEscritos(texto);

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    const response = await saveCierreOfficeContacts(token, mantisBusinessId, clientCode, [
      { officeCode: oficina.officeCode, emails: correos },
    ]).catch(() => null);
    setGuardando(false);
    if (response?.success) onSaved();
    else setError(errorDe(response));
  };

  return (
    <Dialog open={Boolean(oficina)} onOpenChange={(abierto) => !abierto && !guardando && onClose()}>
      <DialogContent className="brinks-scope font-redhat max-w-md">
        <DialogHeader className="text-left">
          <div className="flex min-w-0 items-center gap-1">
            <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">
              Correos de {oficina?.officeName} ({oficina?.officeCode})
            </DialogTitle>
            <BotonGuia guiaId="dialogo-correos-oficina" titulo="Correos de la oficina" texto="Las direcciones a las que se envía el cuadre de esta oficina. Se guardan en la oficina y sirven para todos sus envíos." />
          </div>
          <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
            A estos correos le llega a la oficina su propio cuadre cuando se lo envían desde el panel.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-1">
          <label htmlFor="correos-oficina" className="text-[12px] font-medium text-brinks-muted-foreground">
            Correos
          </label>
          <textarea
            {...guia("Correos", "Escribe uno o varios correos separados por comas. Uno que no sea válido se marca y no deja guardar.")}
            id="correos-oficina"
            rows={4}
            value={texto}
            disabled={guardando}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={"oficina@empresa.com\njefe.oficina@empresa.com"}
            className="min-h-[96px] w-full resize-y rounded-[8px] border bg-card px-2.5 py-1.5 text-[12.5px] text-brinks-foreground outline-none focus:border-brinks-border-strong"
          />
          <span className={cn("text-[11.5px]", invalidos.length ? "text-brinks-danger" : "text-brinks-muted-foreground")}>
            {invalidos.length
              ? `No es un correo válido: ${invalidos.join(", ")}`
              : "Uno por línea, o separados por comas. Vacío: la oficina queda sin correos."}
          </span>
        </div>
        {error ? (
          <p role="alert" className="text-[12px] text-brinks-danger">
            {error}
          </p>
        ) : null}
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-9" disabled={guardando} onClick={onClose}>
            Cancelar
          </Button>
          <Button className="h-9 gap-1.5" disabled={guardando || invalidos.length > 0} onClick={guardar}>
            {guardando ? <Spinner size="sm" /> : <Mail className="h-4 w-4" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The sheet "Descargar plantilla" gives: every office with its addresses today. */
function descargarPlantilla(oficinas, clientCode) {
  const filas = [["Código", "Oficina", "Correos"], ...oficinas.map((o) => [o.officeCode, o.officeName, (o.emails || []).join(", ")])];
  const libro = XLSX.utils.book_new();
  const hoja = XLSX.utils.aoa_to_sheet(filas);
  hoja["!cols"] = [{ wch: 10 }, { wch: 36 }, { wch: 60 }];
  XLSX.utils.book_append_sheet(libro, hoja, "Correos");
  XLSX.writeFile(libro, `correos-oficinas-${clientCode}.xlsx`);
}

export function CargarCorreosExcel({ open, oficinas, token, mantisBusinessId, clientCode, onClose, onSaved }) {
  const [revision, setRevision] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [errorLectura, setErrorLectura] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const leer = async (file) => {
    setArchivo(file ? file.name : null);
    setRevision(null);
    setError(null);
    setErrorLectura(null);
    if (!file) return;
    try {
      const libro = XLSX.read(await file.arrayBuffer());
      const hoja = libro.Sheets[libro.SheetNames[0]];
      const leidas = oficinasDeExcel(XLSX.utils.sheet_to_json(hoja, { header: 1, defval: "" }));
      if (!leidas.length) {
        setErrorLectura("El archivo no trae oficinas: debe tener una columna con el código de la oficina y otra con sus correos.");
        return;
      }
      setRevision(revisarCarga(leidas, oficinas));
    } catch {
      setErrorLectura("No se pudo leer el archivo. Usa un Excel (.xlsx o .xls) o un CSV.");
    }
  };

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    const response = await saveCierreOfficeContacts(
      token,
      mantisBusinessId,
      clientCode,
      revision.listas.map((f) => ({ officeCode: f.codigo, emails: f.correos }))
    ).catch(() => null);
    setGuardando(false);
    if (response?.success) onSaved(response.data.saved.length);
    else setError(errorDe(response));
  };

  const conProblema = revision ? revision.filas.filter((f) => f.problema).length : 0;

  return (
    <Dialog open={open} onOpenChange={(abierto) => !abierto && !guardando && onClose()}>
      <DialogContent className="brinks-scope font-redhat max-w-2xl">
        <DialogHeader className="text-left">
          <div className="flex min-w-0 items-center gap-1">
            <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">Cargar correos de oficinas</DialogTitle>
            <BotonGuia guiaId="dialogo-cargar-correos" titulo="Cargar correos de oficinas" texto="Carga desde un Excel los correos de muchas oficinas a la vez. Antes de guardar se revisa cada fila." />
          </div>
          <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
            Un Excel con una columna “Código” de la oficina y una o más con sus correos. Cada oficina del
            archivo queda con los correos que trae; las que no vienen no cambian.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <label {...guia("Elegir archivo", "Un Excel con una columna \"Código\" de la oficina y una o más columnas de correos.")} className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-[8px] border bg-card px-3 text-[12.5px] font-medium text-brinks-foreground hover:bg-brinks-surface-hover">
            <FileUp className="h-4 w-4" /> {archivo ? "Cambiar archivo" : "Elegir archivo"}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="sr-only"
              aria-label="Archivo de correos"
              disabled={guardando}
              onChange={(e) => leer(e.target.files && e.target.files[0])}
            />
          </label>
          {archivo ? <span className="truncate text-[12px] text-brinks-muted-foreground">{archivo}</span> : null}
          <Button {...guia("Descargar plantilla", "El Excel listo para llenar, con los correos que tiene hoy cada oficina.")} variant="ghost" size="sm" className="ml-auto h-8 gap-1.5 text-[12px]" onClick={() => descargarPlantilla(oficinas, clientCode)}>
            <Download className="h-3.5 w-3.5" /> Descargar plantilla
          </Button>
        </div>

        {errorLectura ? (
          <p role="alert" className="text-[12px] text-brinks-danger">
            {errorLectura}
          </p>
        ) : null}

        {revision ? (
          <>
            <div {...guia("Revisión", "Cada fila del archivo y si está lista: oficina desconocida, sin permiso o un correo no válido. Solo se guarda cuando todas están listas.")} className="max-h-[45vh] overflow-y-auto rounded-[8px] border">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 bg-brinks-surface text-left text-[10.5px] uppercase tracking-[0.04em] text-brinks-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5">Oficina</th>
                    <th className="px-2 py-1.5">Correos</th>
                    <th className="px-2 py-1.5">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {revision.filas.map((f) => (
                    <tr key={f.codigo} className="border-t align-top">
                      <td className="px-2 py-1.5">
                        <span className="font-medium">{f.codigo}</span>
                        {f.nombre ? <span className="block text-brinks-muted-foreground">{f.nombre}</span> : null}
                      </td>
                      <td className="break-all px-2 py-1.5">{f.correos.join(", ") || "—"}</td>
                      <td className={cn("px-2 py-1.5", f.problema ? "text-brinks-danger" : "text-brinks-success")}>
                        {f.problema || (f.antes.length ? "Se actualiza" : "Nueva")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[12px] text-brinks-muted-foreground">
              {revision.filas.length} {revision.filas.length === 1 ? "oficina leída" : "oficinas leídas"}
              {conProblema ? ` · ${conProblema} con problemas: corrige el archivo y vuelve a cargarlo.` : " · todo listo para guardar."}
            </p>
          </>
        ) : null}

        {error ? (
          <p role="alert" className="text-[12px] text-brinks-danger">
            {error}
          </p>
        ) : null}

        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-9" disabled={guardando} onClick={onClose}>
            Cancelar
          </Button>
          <Button className="h-9 gap-1.5" disabled={guardando || !revision?.puedeGuardar} onClick={guardar}>
            {guardando ? <Spinner size="sm" /> : <Mail className="h-4 w-4" />}{" "}
            {revision?.puedeGuardar ? `Guardar ${revision.listas.length} ${revision.listas.length === 1 ? "oficina" : "oficinas"}` : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
